import { Box, Skeleton, SxProps, Typography, alpha } from '@mui/material';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { EventDetails } from '../../../backendsdk';
import { useAlert } from '../../../hooks/alert';
import useCallbackRef from '../../../hooks/callbackRef';
import { useIsVisible } from '../../../hooks/isVisible';
import useProfile from '../../../hooks/profile';
import { VIDEO_CAMERA_ORDER, VideoStreamType, detectStreamCount } from '../../../utils/ImageDimensions';
import { formatLicensePlateWithInternalId } from '../../../utils/Str';
import { isLocationValid } from '../../../utils/location';
import palette from '../../ColorPalette';
import { RTLDirectionContext } from '../../Layout';
import { GpsTrace, IC_EVENTS_LIST, INVALID_SPEED } from './Defs';
import DownloadEventVideoModalComponent from './DownloadEventVideoModal';
import CroppedVideo, { LayoutOptions, STREAMS_GRID_DEFS, getVideoStreams } from './EventVideoStreams';
import VideoControlsComponent from './VideoControls';
import { STORAGE_KEY_PLAYBACK_RATE, getStoredPlaybackRate } from './videoPlaybackRateStorage';

export interface EventDetailsComponentProps {
    eventId: number;
    eventType: string;
    eventTimestamp?: number;
    licensePlate?: string;
    artifact: string;
    eventDetails?: EventDetails;
    viewMode: LayoutOptions;
    setPercentVideoPlayed?: CallableFunction;
    overlay?: string;
    gpsTrace?: Array<GpsTrace>;
    setAllowEdit?: CallableFunction;
    currentLocation?: string;
    setCurrentLocation?: CallableFunction;
    currentTime?: number;
    setCurrentTime?: CallableFunction;
    setVideoDuration?: CallableFunction;
    isGeneratingVideo?: boolean;
    controls?: boolean;
    onPlay?: CallableFunction;
    onPause?: CallableFunction;
    isRendered?: boolean;
    setIsLoaded?: CallableFunction;
    onLoadedMetadata?: CallableFunction;
    onDurationChange?: CallableFunction;
    onTimeUpdate?: CallableFunction;
    containerStyle?: SxProps;
    children?: React.ReactNode;
    setIsError?: CallableFunction;
    tags?: Array<string>;
}

const EventVideoComponent: React.FC<EventDetailsComponentProps> = (props: EventDetailsComponentProps) => {
    const { eventId, eventType, artifact, gpsTrace } = props;
    const [primaryVideo, setPrimaryVideo] = useState<VideoStreamType>(
        props.viewMode != '1x1+3x1' || IC_EVENTS_LIST.includes(eventType)
            ? VideoStreamType.InCabinCamera
            : VideoStreamType.FrontFacingCamera,
    );
    const [isPlaying, setIsPlaying] = useState<boolean>(props.viewMode == '1x2');
    const [seekLocation, setSeekLocation] = useState<number>(0);
    const [fullScreen, setFullScreen] = useState<boolean>(false);
    const [showOverlay, setShowOverlay] = useState<boolean>(true);
    const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
    const [playbackRate, setPlaybackRate] = useState<number>(getStoredPlaybackRate);
    const [speedMeterPerSecond, setSpeedMeterPerSecond] = useState<number>(INVALID_SPEED);
    const [streams, setStreams] = useState<Record<string, [number, number, number, number]>>();
    const [detectedStreamCount, setDetectedStreamCount] = useState<number | null>(null);
    const { profile } = useProfile();
    const { t } = useTranslation();
    const { isVisible, targetRef } = useIsVisible<HTMLDivElement>({ root: null, threshold: 1 }, false);
    const isRTL = useContext(RTLDirectionContext);
    const location = useLocation();
    const [primaryVideoRef, primaryNode] = useCallbackRef<HTMLVideoElement>();
    const [secondaryVideoRef1, secondaryNode1] = useCallbackRef<HTMLVideoElement>();
    const [secondaryVideoRef2, secondaryNode2] = useCallbackRef<HTMLVideoElement>();
    const [secondaryVideoRef3, secondaryNode3] = useCallbackRef<HTMLVideoElement>();
    const secondaryRefs = [secondaryVideoRef1, secondaryVideoRef2, secondaryVideoRef3];
    const secondaryNodes = [secondaryNode1, secondaryNode2, secondaryNode3];
    const [alertElement, setAlert] = useAlert();

    const numberOfStreams = Object.keys(streams || {}).length || 2;
    const aspectRatio = '16 / 9';

    const isVerticalMonitor =
        !(props.viewMode == '2x1') &&
        fullScreen &&
        document.documentElement.clientWidth > document.documentElement.clientHeight;
    const artifactWithHash = location.hash ? `${artifact}${location.hash}` : artifact;

    const handleStart = () => {
        // catch error in case the video already paused (play is async)
        [primaryNode, ...secondaryNodes].forEach((nodeIter) => nodeIter?.play().catch(() => null));

        setIsPlaying(true);
        if (props.onPlay) {
            props.onPlay();
        }
    };

    const handleStop = () => {
        [primaryNode, ...secondaryNodes].forEach((nodeIter) => nodeIter?.pause());
        setIsPlaying(false);
    };

    const videoDuration = primaryNode?.duration || 0;

    const speedFactor = profile.locale === 'il' ? 1 : 0.621371;
    const speedText = profile.locale === 'il' ? 'kmh' : 'mph';

    useEffect(() => {
        if (primaryNode) {
            primaryNode.playbackRate = playbackRate;
        }
        secondaryNodes.forEach((nodeIter) => {
            if (nodeIter) {
                nodeIter.playbackRate = playbackRate;
            }
        });
    }, [playbackRate, primaryNode, secondaryNode1, secondaryNode2, secondaryNode3]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_PLAYBACK_RATE, String(playbackRate));
        } catch {
            // ignore storage errors (e.g. private mode)
        }
    }, [playbackRate]);

    useEffect(() => {
        secondaryNodes.forEach((nodeIter) => {
            if (nodeIter) {
                nodeIter.currentTime = primaryNode?.currentTime || 0;
                nodeIter.playbackRate = primaryNode?.playbackRate || 1;
                if (isPlaying && nodeIter.paused) {
                    nodeIter.play().catch(() => null);
                } else if (!isPlaying && !nodeIter.paused) {
                    nodeIter.pause();
                }
            }
        });
    }, [props.eventId, secondaryNode1, secondaryNode2, secondaryNode3, primaryNode, isPlaying]);

    useEffect(() => {
        if (props.eventDetails) {
            getVideoStreams(props.eventDetails, (fileContent: string) => {
                const header = fileContent
                    .split('\n')
                    .filter((line) => line.trim() !== '' && line.includes('streams'))[0];
                if (!!header) {
                    const parsedStreams = JSON.parse(header.replaceAll(';', '')).streams;
                    setStreams(parsedStreams);
                    // If primaryVideo is not in streams, change it to the first key
                    if (parsedStreams && !parsedStreams[primaryVideo]) {
                        const firstStreamKey = Object.keys(parsedStreams)[0] as VideoStreamType;
                        if (firstStreamKey) {
                            setPrimaryVideo(firstStreamKey);
                        }
                    }
                }
            });
        }
    }, [props.eventDetails]);

    useEffect(() => {
        if (isPlaying) {
            [primaryNode, ...secondaryNodes].forEach((nodeIter) => nodeIter?.play().catch(() => null));
        } else {
            [primaryNode, ...secondaryNodes].forEach((nodeIter) => nodeIter?.pause());
        }
        secondaryNodes.forEach((nodeIter) => {
            if (!!nodeIter && !!primaryNode?.currentTime) {
                nodeIter.currentTime = primaryNode?.currentTime;
                nodeIter.playbackRate = primaryNode?.playbackRate || 1;
            }
        });
    }, [primaryVideo]);

    useEffect(() => {
        if (props.viewMode === '2x1' || !props.controls) {
            if (isVisible) {
                handleStart();
            } else {
                handleStop();
            }
        }
    }, [isVisible]);

    useEffect(() => {
        if (props.setVideoDuration) {
            props.setVideoDuration(primaryNode?.duration);
        }
    }, [primaryNode?.duration]);

    const handleSeekerChange = (_event: Event, value: number | number[]) => {
        let newValue = value as number;
        if (newValue < 3) {
            newValue = 0;
        }
        setSeekLocation(newValue);
        const newTime = (newValue * videoDuration) / 100;
        const newTimeRounded = Math.round(newTime * 100) / 100;
        [primaryNode, ...secondaryNodes].forEach((nodeIter) => {
            if (nodeIter) {
                nodeIter.currentTime = newTimeRounded;
            }
        });
    };

    const showVehicleInternalId = profile.customer.settings.show_vehicle_internal_id ?? false;
    const vehicleOverlayLabel = props.eventDetails
        ? formatLicensePlateWithInternalId(
              props.eventDetails.device.license_plate,
              props.eventDetails.device.internal_id,
              showVehicleInternalId,
          )
        : props.licensePlate ?? '';

    const fontSize = fullScreen ? 18 : 12;
    const videoOverlay = (
        <Box
            sx={{
                position: 'absolute',
                top: 10,
                insetInlineEnd: 10,
                backgroundColor: alpha(palette.black, 0.3),
                py: 0.5,
                px: 1,
                zIndex: 1200,
                display: 'flex',
                flexDirection: 'column',
                alignItems: isRTL ? 'flex-end' : 'flex-start',
                borderRadius: 2,
            }}
        >
            {!!props.overlay ? (
                <Typography sx={{ fontSize, color: palette.white }}>{props.overlay}</Typography>
            ) : (
                <>
                    <Typography sx={{ fontSize, color: palette.white }}>
                        {t(`content.cameras.${primaryVideo}`)}
                    </Typography>
                    {!!vehicleOverlayLabel ? (
                        <Typography sx={{ fontSize, color: 'oklch(83.7% 0.128 66.29)' }}>
                            {t('content.events.vehicle')}: {vehicleOverlayLabel}
                        </Typography>
                    ) : null}
                    {!!props.eventDetails || !!props.eventTimestamp ? (
                        <Typography sx={{ fontSize, color: 'oklch(79.2% 0.209 151.711)' }}>
                            {dayjs
                                .unix(
                                    (props.eventDetails?.timestamp || props.eventTimestamp || 0) +
                                        (props.currentTime || 0),
                                )
                                .format(profile.dateTimeFormat)}
                        </Typography>
                    ) : null}
                    {!!props.currentLocation ? (
                        <Typography
                            sx={{
                                fontSize,
                                color: 'oklch(70.7% 0.165 254.624)',
                            }}
                        >
                            {props.currentLocation.replace(':', ', ')}
                        </Typography>
                    ) : null}
                    {speedMeterPerSecond != INVALID_SPEED ? (
                        <Typography sx={{ fontSize, color: 'oklch(85.2% 0.199 91.936)' }}>
                            {`${t('content.events.speed')}: ${Math.round(speedMeterPerSecond * 3.6 * speedFactor)} ${t(
                                `content.events.${speedText}`,
                            )}`}
                        </Typography>
                    ) : null}
                </>
            )}
        </Box>
    );

    const secondaryCameraOverlay = (streamName: string) => (
        <Box
            sx={{
                position: 'absolute',
                top: 5,
                insetInlineEnd: 5,
                backgroundColor: alpha(palette.black, 0.3),
                py: 0.5,
                px: 1,
                zIndex: 1200,
                display: 'flex',
                flexDirection: 'column',
                alignItems: isRTL ? 'flex-end' : 'flex-start',
                borderRadius: 2,
            }}
        >
            <Typography sx={{ fontSize: fullScreen ? 18 : 12, color: palette.white }}>
                {t(`content.cameras.${streamName}`)}
            </Typography>
        </Box>
    );

    useEffect(() => {
        // make sure to update the full screen state when the user exits full screen mode
        const syncFullScreenState = () => {
            if (document.fullscreenElement === null) {
                setShowOverlay(true);
            }
            setFullScreen(document.fullscreenElement !== null);
        };
        document.addEventListener('fullscreenchange', syncFullScreenState);

        return () => {
            document.removeEventListener('fullscreenchange', syncFullScreenState);
        };
    }, []);

    if (props.isGeneratingVideo) {
        return (
            <Box sx={{ width: '100%', aspectRatio }}>
                <Skeleton sx={{ width: '100%', height: '100%' }} variant="rectangular" animation="wave" />
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                    }}
                >
                    <Typography variant="overline">{t('content.events.generating_video')}</Typography>
                </Box>
            </Box>
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sliderStyles: any = {
        color: palette.neutral[50],
        mx: 1,
        '& .MuiSlider-mark': {
            backgroundColor: palette.accent,
            opacity: 1,
            zIndex: 5,
        },
    };

    if (isRTL) {
        sliderStyles['& .MuiSlider-thumb'] = {
            transform: 'translateX(5px) translateY(-10px)',
        };
    } else {
        sliderStyles['& .MuiSlider-mark'].transform = 'scale(4) translateY(-15%) translateX(-15%)';
    }

    const singleStream = Object.keys(streams || {}).length == 1;
    const viewMode = singleStream ? '1x1' : props.viewMode;
    const streamsLayoutKey = props.controls ? `${viewMode}+controls` : viewMode;
    const streamDef = STREAMS_GRID_DEFS[streamsLayoutKey as keyof typeof STREAMS_GRID_DEFS];

    // Determine otherStreams based on detected stream count or existing streams
    let otherStreams = VIDEO_CAMERA_ORDER.filter((stream) => stream !== primaryVideo && !!streams?.[stream]);

    // If no streams detected from metadata, use detected stream count to guess
    if (otherStreams.length == 0 && !singleStream && props.viewMode != '1x1') {
        const streamCount = detectedStreamCount || Object.keys(streams || {}).length || 2;
        // Get streams from VIDEO_CAMERA_ORDER based on detected count, excluding primaryVideo
        const availableStreams = VIDEO_CAMERA_ORDER.slice(0, streamCount).filter((stream) => stream !== primaryVideo);
        otherStreams =
            availableStreams.length > 0
                ? availableStreams
                : [
                      // Fallback: if primary is FF, use IC, otherwise use FF
                      primaryVideo == VideoStreamType.FrontFacingCamera
                          ? VideoStreamType.InCabinCamera
                          : VideoStreamType.FrontFacingCamera,
                  ];
    }

    return (
        <Box
            id="event-video-dialog"
            ref={targetRef}
            sx={{
                width: isVerticalMonitor ? undefined : '100%',
                height: isVerticalMonitor ? '100vh' : undefined,
                display: 'grid',
                ...streamDef.grid,
                direction: isRTL ? 'rtl' : 'ltr',
                gap: 0.1,
                zIndex: 3,
                ...(props.containerStyle || {}),
            }}
        >
            {alertElement}
            {showDownloadModal ? (
                <DownloadEventVideoModalComponent
                    eventDetails={props.eventDetails}
                    eventId={eventId}
                    onClose={() => setShowDownloadModal(false)}
                    isInCabinPrimary={primaryVideo == VideoStreamType.InCabinCamera}
                    setAlert={setAlert}
                    numberOfStreams={numberOfStreams}
                    artifact={artifact}
                />
            ) : null}
            <Box
                id={`${eventId}-main-video-container`}
                data-testid={`${eventId}-main-video-container`}
                sx={{
                    gridArea: 'main',
                    aspectRatio: viewMode == '1x2' ? '4 / 3' : '16 / 9',
                }}
            >
                <Box
                    sx={{
                        width: streamDef.mainStreamWidth(targetRef),
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // on 1x2 mode, the main video is ic, which is 4/3, so we need to adjust the aspect ratio
                        aspectRatio: viewMode == '1x2' ? '4 / 3' : '16 / 9',
                    }}
                >
                    {showOverlay ? videoOverlay : null}
                    {/* Primary Video */}
                    <CroppedVideo
                        id={`${eventId}-video`}
                        data-testid={`primary-video-${primaryVideo}`}
                        src={artifactWithHash}
                        stream={primaryVideo}
                        streams={streams || {}}
                        videoRef={primaryVideoRef}
                        videoNode={primaryNode}
                        applyBorderRadius={props.viewMode == '1x1+3x1'}
                        applyXScale={props.viewMode == '1x1+3x1'}
                        videoProps={{
                            autoPlay: isPlaying,
                            muted: true,
                            loop: true,
                            onTimeUpdate: (event) => {
                                const { currentTime, duration } = event.currentTarget;
                                if (props.setCurrentTime) {
                                    props.setCurrentTime(currentTime);
                                }
                                setSeekLocation((100 * currentTime) / duration);
                                if (gpsTrace && gpsTrace.length > 0 && duration !== undefined) {
                                    const idx = Math.round(Math.min(currentTime / duration, 1) * (gpsTrace.length - 1));
                                    setSpeedMeterPerSecond(gpsTrace[idx].speed);
                                    const lat = gpsTrace[idx].lat.toFixed(6).toString();
                                    const lng = gpsTrace[idx].lng.toFixed(6).toString();
                                    if (isLocationValid(`${lat}:${lng}`)) {
                                        if (props.setCurrentLocation) {
                                            props.setCurrentLocation(`${lat}:${lng}`);
                                        }
                                    }
                                }
                                if (props.setPercentVideoPlayed !== undefined && videoDuration !== undefined) {
                                    props.setPercentVideoPlayed(currentTime / duration);
                                }
                                if (props.onTimeUpdate) {
                                    props.onTimeUpdate(event);
                                }
                            },
                            onCanPlay: () => {
                                if (props.setAllowEdit) {
                                    props.setAllowEdit(true);
                                }
                            },
                            onLoadedMetadata: (event) => {
                                const video = event.currentTarget;
                                // Detect stream count from video dimensions
                                if (video.videoWidth && video.videoHeight) {
                                    const streamCount = detectStreamCount(video.videoWidth, video.videoHeight);
                                    setDetectedStreamCount(streamCount);
                                }
                                if (props.onLoadedMetadata) {
                                    props.onLoadedMetadata(event);
                                }
                            },
                            onDurationChange: (event) => {
                                if (props.onDurationChange) {
                                    props.onDurationChange(event);
                                }
                            },
                            onClick: () => {
                                if (props.controls) {
                                    isPlaying ? handleStop() : handleStart();
                                }
                            },
                            onDoubleClick: () => {
                                if (props.controls) {
                                    fullScreen ? document.exitFullscreen() : targetRef.current?.requestFullscreen?.();
                                    setFullScreen((prev) => !prev);
                                }
                            },
                            onPause: () => {
                                if (props.onPause) {
                                    props.onPause();
                                }
                            },
                            onError: () => {
                                if (props.setIsError) {
                                    props.setIsError(true);
                                }
                            },
                            onRateChange: (event) => {
                                secondaryNodes.forEach((node) => {
                                    if (node) {
                                        const target = event.target as HTMLVideoElement;
                                        node.playbackRate = target.playbackRate;
                                    }
                                });
                            },
                        }}
                    />

                    {props.children}
                </Box>
            </Box>
            {otherStreams.map((stream, idx) => {
                return (
                    <Box
                        sx={{
                            position: 'relative',
                            gridArea: `secondary${idx}`,
                            width: streamDef.secondaryStreamWidth(targetRef),
                            aspectRatio: '16 / 9',
                        }}
                        key={stream}
                    >
                        {showOverlay ? secondaryCameraOverlay(stream) : null}
                        <CroppedVideo
                            key={stream}
                            id={`${eventId}-video-${stream}`}
                            data-testid={`${eventId}-video-${stream}`}
                            src={artifactWithHash}
                            stream={stream}
                            streams={streams || {}}
                            videoRef={secondaryRefs[idx]}
                            videoNode={secondaryNodes[idx]}
                            applyBorderRadius={props.viewMode == '1x1+3x1'}
                            applyXScale={props.viewMode == '1x1+3x1'}
                            applyHover={props.viewMode == '1x1+3x1'}
                            videoProps={{
                                autoPlay: isPlaying,
                                muted: true,
                                loop: true,
                                onClick: () => {
                                    if (props.viewMode == '1x1+3x1') {
                                        setPrimaryVideo(stream);
                                    }
                                },
                            }}
                        />
                    </Box>
                );
            })}
            {props.controls ? (
                <Box sx={{ gridArea: 'controls' }}>
                    <VideoControlsComponent
                        isPlaying={isPlaying}
                        handleStart={handleStart}
                        handleStop={handleStop}
                        seekLocation={seekLocation}
                        handleSeekerChange={handleSeekerChange}
                        videoDuration={videoDuration}
                        targetRef={targetRef}
                        setShowDownloadModal={setShowDownloadModal}
                        showOverlay={showOverlay}
                        setShowOverlay={setShowOverlay}
                        hideSecondary={!!videoOverlay}
                        fullScreen={fullScreen}
                        setFullScreen={setFullScreen}
                        playbackRate={playbackRate}
                        onPlaybackRateChange={setPlaybackRate}
                    />
                </Box>
            ) : null}
        </Box>
    );
};

export default EventVideoComponent;
